import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
	FiAlertTriangle,
	FiArrowRight,
	FiCheckCircle,
	FiChevronLeft,
	FiChevronRight,
	FiEdit3,
	FiRefreshCw,
	FiSearch,
	FiXCircle
} from "react-icons/fi";
import { usePageHeader } from "../hooks/usePageHeader";
import { client } from "../Clients";
import { LoadingSpinner } from "../Components/LoadingSpinner";
import {
	getOverviewPage,
	type TranslationOverviewItem,
	type TranslationOverviewSummary,
	type TranslationOverviewTypeCount
} from "./Traduction/translationsClient";

// Target languages the BCC can publish into; the actually offered pills are intersected with
// the "languages" singleton so editors only see languages enabled for the site.
const TARGET_LANGUAGES = [
	{ code: "en", label: "English", flag: "gb" },
	{ code: "nl", label: "Nederlands", flag: "nl" },
	{ code: "de", label: "Deutsch", flag: "de" },
	{ code: "es", label: "Español", flag: "es" },
	{ code: "pt", label: "Português", flag: "pt" },
	{ code: "ar", label: "العربية", flag: "sa" },
	{ code: "sw", label: "Swahili", flag: "tz" },
	{ code: "ln", label: "Lingala", flag: "cd" }
];

type StatusFilter = "all" | "attention" | "done";

const detailUrl = (item: TranslationOverviewItem, lang: string): string => {
	const params = new URLSearchParams();
	if (item.slug) params.set("slug", item.slug);
	params.set("lang", lang);
	return `/traduction/${encodeURIComponent(item.schemaKey)}?${params.toString()}`;
};

const PAGE_SIZE = 50;

/**
 * Translation workspace overview: per-document field coverage (ok / stale / missing / manual)
 * in one target language, fed by GET /api/translations/overview. Rows are paged, filtered and
 * searched SERVER-side : the corpus is thousands of documents, so the page only ever holds one
 * page of rows while the header cards show corpus-wide aggregates from the same response.
 * Each row opens the per-field detail workspace where the actual translating happens.
 */
const Traduction: React.FC = () => {
	usePageHeader({ title: "Traduction" });

	const [items, setItems] = useState<TranslationOverviewItem[]>([]);
	const [summary, setSummary] = useState<TranslationOverviewSummary | null>(null);
	const [types, setTypes] = useState<TranslationOverviewTypeCount[]>([]);
	const [filteredTotal, setFilteredTotal] = useState(0);
	const [page, setPage] = useState(1);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [search, setSearch] = useState("");
	const [query, setQuery] = useState(""); // debounced copy of `search` actually sent to the API
	const [filterType, setFilterType] = useState<string>("all");
	const [filterStatus, setFilterStatus] = useState<StatusFilter>("all");
	const [targetLang, setTargetLang] = useState("en");
	const [enabledCodes, setEnabledCodes] = useState<string[] | null>(null);
	// Out-of-order responses (fast typing, page flips) must not overwrite newer ones.
	const requestSeq = useRef(0);

	// Debounce the search box; resetting the page in the SAME state transition as the filter/
	// query change means `load` is rebuilt once (with page 1) : never a doomed stale-page fetch
	// followed by a page-1 fetch.
	useEffect(() => {
		const handle = window.setTimeout(() => {
			setQuery(search);
			setPage(1);
		}, 300);
		return () => window.clearTimeout(handle);
	}, [search]);

	// Changing the target language or a filter resets to page 1 atomically with the change.
	const changeLanguage = useCallback((code: string) => {
		setTargetLang(code);
		setPage(1);
	}, []);
	const changeType = useCallback((value: string) => {
		setFilterType(value);
		setPage(1);
	}, []);
	const changeStatus = useCallback((value: StatusFilter) => {
		setFilterStatus(value);
		setPage(1);
	}, []);

	// Languages enabled in the "languages" singleton; null = unknown → show the full list.
	useEffect(() => {
		let cancelled = false;
		client.content
			.apiContentSchemaKeyLanguageGet("languages", "default")
			.then((res) => {
				const configured = (res.data?.current?.data as { languages?: Array<{ code?: string; isDisabled?: boolean }> } | undefined)
					?.languages;
				if (!cancelled && Array.isArray(configured)) {
					setEnabledCodes(
						configured.filter((l) => l?.code && !l.isDisabled).map((l) => l.code!.trim().toLowerCase())
					);
				}
			})
			.catch(() => {
				/* keep the full list */
			});
		return () => {
			cancelled = true;
		};
	}, []);

	const languages = useMemo(() => {
		if (!enabledCodes) return TARGET_LANGUAGES;
		const filtered = TARGET_LANGUAGES.filter((l) => enabledCodes.includes(l.code));
		return filtered.length > 0 ? filtered : TARGET_LANGUAGES;
	}, [enabledCodes]);

	const load = useCallback(async () => {
		const seq = ++requestSeq.current;
		setLoading(true);
		setError(null);
		try {
			const res = await getOverviewPage(targetLang, {
				page,
				pageSize: PAGE_SIZE,
				q: query,
				schemaKey: filterType === "all" ? undefined : filterType,
				status: filterStatus
			});
			if (seq !== requestSeq.current) return;
			setItems(res.items ?? []);
			setSummary(res.summary ?? null);
			setTypes(res.types ?? []);
			setFilteredTotal(res.filteredTotal ?? 0);
		} catch (err) {
			if (seq !== requestSeq.current) return;
			console.error(err);
			setError("Impossible de charger l'état des traductions.");
		} finally {
			if (seq === requestSeq.current) setLoading(false);
		}
	}, [targetLang, page, query, filterType, filterStatus]);

	useEffect(() => {
		load();
	}, [load]);

	const totals = useMemo(() => {
		const ok = summary?.ok ?? 0;
		const totalFields = summary?.totalFields ?? 0;
		return {
			ok,
			stale: summary?.stale ?? 0,
			missing: summary?.missing ?? 0,
			manual: summary?.manual ?? 0,
			percent: totalFields > 0 ? Math.round((ok / totalFields) * 100) : 0
		};
	}, [summary]);

	const pageCount = Math.max(1, Math.ceil(filteredTotal / PAGE_SIZE));
	const rangeStart = filteredTotal === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
	const rangeEnd = Math.min(page * PAGE_SIZE, filteredTotal);
	const targetLangObj = languages.find((l) => l.code === targetLang) ?? languages[0];

	// If the filtered set shrinks below the current page (e.g. Actualiser after colleagues
	// translated rows on the active filter), snap back to the last real page instead of
	// stranding the user on an empty list.
	useEffect(() => {
		if (!loading && filteredTotal > 0 && page > pageCount) setPage(pageCount);
	}, [loading, filteredTotal, page, pageCount]);

	// Full-screen spinner only on the FIRST load (nothing to show yet); later refetches keep
	// the current rows on screen, dimmed, so a page flip or search never blanks the table.
	const initialLoading = loading && items.length === 0 && !error;
	const refreshing = loading && items.length > 0;

	return (
		<div className="min-h-full space-y-6 overflow-x-hidden p-4 sm:p-6">
			{/* Header */}
			<div className="flex flex-wrap items-center justify-between gap-3">
				<div>
					<h1 className="text-2xl font-bold text-slate-900">Traductions</h1>
					<p className="text-sm text-slate-500">
						Couverture champ par champ du contenu français : seuls les champs modifiés sont à retraduire.
					</p>
				</div>
				<button
					type="button"
					onClick={load}
					disabled={loading}
					className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-60"
				>
					<FiRefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Actualiser
				</button>
			</div>

			{error && <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>}

			{/* Language pills + aggregate counters */}
			<div className="grid gap-4 lg:grid-cols-[auto_1fr]">
				<div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
					<p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Langue cible</p>
					<div className="mt-2 flex flex-wrap gap-2">
						{languages.map((l) => (
							<button
								key={l.code}
								type="button"
								onClick={() => changeLanguage(l.code)}
								className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition ${
									targetLang === l.code
										? "bg-blue-600 text-white"
										: "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
								}`}
							>
								<span className={`fi fi-${l.flag} h-3 w-4 rounded-sm`} />
								{l.label}
							</button>
						))}
					</div>
					<p className="mt-3 text-xs text-slate-500">
						Repli : un champ non traduit reste affiché en français sur le site.
					</p>
				</div>

				<div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
					<div className="flex flex-wrap items-center justify-between gap-2">
						<p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
							Champs traduits vers {targetLangObj?.label ?? targetLang}
						</p>
						<p className="text-sm font-bold tabular-nums text-slate-900">{totals.percent}%</p>
					</div>
					<div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-100">
						<div
							className={`h-2 rounded-full transition-all ${
								totals.percent >= 80 ? "bg-emerald-500" : totals.percent >= 40 ? "bg-amber-500" : "bg-rose-500"
							}`}
							style={{ width: `${totals.percent}%` }}
						/>
					</div>
					<div className="mt-4 flex flex-wrap gap-2">
						<span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-700">
							<FiCheckCircle className="h-3.5 w-3.5" /> {totals.ok} à jour
						</span>
						<span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-bold text-amber-700">
							<FiAlertTriangle className="h-3.5 w-3.5" /> {totals.stale} obsolètes
						</span>
						<span className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-rose-50 px-2.5 py-1 text-xs font-bold text-rose-700">
							<FiXCircle className="h-3.5 w-3.5" /> {totals.missing} manquants
						</span>
						<span className="inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-indigo-50 px-2.5 py-1 text-xs font-bold text-indigo-700">
							<FiEdit3 className="h-3.5 w-3.5" /> {totals.manual} manuels
						</span>
					</div>
				</div>
			</div>

			{/* Filters */}
			<div className="flex flex-wrap items-center gap-3">
				<div className="relative flex-1">
					<FiSearch className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
					<input
						type="text"
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						placeholder="Rechercher un contenu…"
						className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm text-slate-700 focus:border-slate-400 focus:outline-none"
					/>
				</div>
				<select
					value={filterType}
					onChange={(e) => changeType(e.target.value)}
					className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700"
				>
					<option value="all">Tous les types</option>
					{types.map((t) => (
						<option key={t.schemaKey} value={t.schemaKey}>
							{t.schemaKey} ({t.count})
						</option>
					))}
				</select>
				<div className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white p-0.5">
					{(
						[
							{ key: "all", label: `Tous (${summary?.totalDocs ?? 0})` },
							{ key: "attention", label: `À traduire (${summary?.attentionDocs ?? 0})` },
							{ key: "done", label: `À jour (${summary?.translatedDocs ?? 0})` }
						] as Array<{ key: StatusFilter; label: string }>
					).map((s) => (
						<button
							key={s.key}
							type="button"
							onClick={() => changeStatus(s.key)}
							className={`rounded-md px-3 py-1.5 text-xs font-semibold transition ${
								filterStatus === s.key ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
							}`}
						>
							{s.label}
						</button>
					))}
				</div>
			</div>

			{/* Document list (one server-side page) */}
			{initialLoading ? (
				<div className="flex items-center justify-center py-16">
					<LoadingSpinner size="lg" />
				</div>
			) : filteredTotal === 0 ? (
				<div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-6 py-16 text-center text-sm text-slate-500">
					Aucun contenu ne correspond aux filtres.
				</div>
			) : (
				<div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
					{/* Refetch keeps the current rows visible, dimmed, with an unobtrusive top bar : the table never blanks on a page flip / filter change / search keystroke. */}
					{refreshing && (
						<div className="absolute inset-x-0 top-0 z-10 h-0.5 overflow-hidden bg-blue-100">
							<div className="h-full w-1/3 animate-pulse rounded-full bg-blue-500" />
						</div>
					)}
					<table className={`w-full text-sm transition-opacity ${refreshing ? "opacity-60" : ""}`}>
						<thead>
							<tr className="bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-widest text-slate-500">
								<th className="px-4 py-3">Contenu</th>
								<th className="px-4 py-3">Type</th>
								<th className="px-4 py-3">Avancement</th>
								<th className="px-4 py-3">État des champs</th>
								<th className="px-4 py-3 text-right">Actions</th>
							</tr>
						</thead>
						<tbody className="divide-y divide-slate-100">
							{items.map((item) => {
								const percent = item.total > 0 ? Math.round((item.ok / item.total) * 100) : 0;
								const url = detailUrl(item, targetLang);
								return (
									<tr key={`${item.schemaKey}/${item.contentId}`} className="hover:bg-slate-50">
										<td className="max-w-xs px-4 py-3">
											<Link to={url} className="font-semibold text-slate-900 hover:text-blue-700 hover:underline">
												{item.title || item.slug || item.contentId}
											</Link>
											{item.slug && <p className="truncate text-xs text-slate-500">/{item.slug}</p>}
										</td>
										<td className="px-4 py-3">
											<span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-bold uppercase text-slate-600">
												{item.schemaKey}
											</span>
										</td>
										<td className="w-40 px-4 py-3">
											<div className="flex items-center gap-2">
												<div className="h-1.5 w-20 overflow-hidden rounded-full bg-slate-100">
													<div
														className={`h-1.5 rounded-full ${
															percent >= 100 ? "bg-emerald-500" : percent >= 40 ? "bg-amber-500" : "bg-rose-500"
														}`}
														style={{ width: `${percent}%` }}
													/>
												</div>
												<span className="text-xs font-bold tabular-nums text-slate-700">{percent}%</span>
											</div>
										</td>
										<td className="px-4 py-3">
											<div className="flex flex-wrap gap-1.5">
												{item.ok > 0 && (
													<span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700">
														{item.ok} ok
													</span>
												)}
												{item.stale > 0 && (
													<span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-bold text-amber-700">
														{item.stale} obsolète{item.stale > 1 ? "s" : ""}
													</span>
												)}
												{item.missing > 0 && (
													<span className="rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-[10px] font-bold text-rose-700">
														{item.missing} manquant{item.missing > 1 ? "s" : ""}
													</span>
												)}
												{item.manual > 0 && (
													<span className="rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[10px] font-bold text-indigo-700">
														{item.manual} manuel{item.manual > 1 ? "s" : ""}
													</span>
												)}
												{item.total === 0 && <span className="text-xs text-slate-400">Aucun champ traduisible</span>}
											</div>
										</td>
										<td className="px-4 py-3 text-right">
											<Link
												to={url}
												className="inline-flex items-center gap-1 rounded-lg border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700 hover:bg-blue-100"
											>
												Traduire <FiArrowRight className="h-3 w-3" />
											</Link>
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
					{/* Pagination */}
					<div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 bg-slate-50/60 px-4 py-3">
						<p className="text-xs text-slate-500">
							<span className="font-semibold tabular-nums text-slate-700">
								{rangeStart}-{rangeEnd}
							</span>{" "}
							sur <span className="font-semibold tabular-nums text-slate-700">{filteredTotal}</span> contenus
						</p>
						<div className="flex items-center gap-2">
							<button
								type="button"
								onClick={() => setPage((p) => Math.max(1, p - 1))}
								disabled={page <= 1 || loading}
								className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
							>
								<FiChevronLeft className="h-3.5 w-3.5" /> Précédent
							</button>
							<span className="text-xs font-semibold tabular-nums text-slate-600">
								Page {page} / {pageCount}
							</span>
							<button
								type="button"
								onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
								disabled={page >= pageCount || loading}
								className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
							>
								Suivant <FiChevronRight className="h-3.5 w-3.5" />
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default Traduction;
